import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { addDoc, collection, doc, setDoc } from 'firebase/firestore';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatFabButton } from '@angular/material/button';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { ImportExportComponent } from '../settings/dialogs/import-export/import-export.component';
import { exportJsonToExcel, readExcelFile } from '../globals';
import { dataStore, db } from '../app.component';
import { WarningDialog } from '../global-dialogs/warning/warning.component';
import { VehiclesComponent } from './tabs/vehicles/vehicles.component';
import { VehicleGroupsComponent } from './tabs/vehicle-groups/vehicle-groups.component';
import { RatesComponent } from './tabs/rates/rates.component';

@Component({
  selector: 'app-transport',
  templateUrl: './transport.component.html',
  styleUrl: './transport.component.scss',
  imports: [
    MatIcon,
    MatIconButton,
    MatTabGroup,
    MatTab,
    MatTabLabel,
    VehiclesComponent,
    VehicleGroupsComponent,
    RatesComponent,
    MatFabButton,
  ],
})
export class TransportComponent implements OnInit, OnDestroy {
  @ViewChild('uploader', { static: false }) uploader: ElementRef;
  @ViewChildren('tab') tabComponents: QueryList<any>;
  selectedIndex = signal(0);
  processing = signal(false);
  fileUploaded: File;

  tabs = [
    {
      key: 'vehicles',
      name: 'Voertuigen',
      url: 'vehicles',
      icon: 'local_taxi',
    },
    {
      key: 'vehicleGroups',
      name: 'Voertuiggroepen',
      url: 'vehicle-groups',
      icon: 'emoji_transportation',
    },
    {
      key: 'rates',
      name: 'Tarieven',
      url: 'rates',
      icon: 'account_balance_wallet',
    },
  ];

  private routeSubscription: Subscription = new Subscription();

  get activeTab(): any {
    return this.tabComponents?.toArray()[this.selectedIndex()];
  }

  get selectedTabKey() {
    return this.tabs[this.selectedIndex()]?.key;
  }

  constructor(
    public dialog: MatDialog,
    public router: Router,
    public snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.routeSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateSelectedIndex();
      });
    this.updateSelectedIndex();
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
  }

  updateUrl() {
    const tab = this.tabs[this.selectedIndex()];
    if (!tab) {
      return;
    }
    this.router.navigate([`/transport/${tab.url}`]);
  }

  updateSelectedIndex() {
    const activeUrl = this.getActiveTabUrlFromRoute();
    const index = this.tabs.findIndex((tab) => tab.url === activeUrl);
    if (index >= 0) {
      this.selectedIndex.set(index);
      return;
    }
    this.selectedIndex.set(0);
  }

  private getActiveTabUrlFromRoute(): string | null {
    return this.tabs.find((tab) => this.router.url.includes(`/transport/${tab.url}`))?.url ?? null;
  }

  openImportExport() {
    if (this.processing()) {
      return;
    }
    const dialogRef = this.dialog.open(ImportExportComponent, {
      width: '350px',
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (!result?.type) {
        return;
      }
      if (result.type === 'Export') {
        await this.exportData();
      }
      if (result.type === 'Import') {
        this.uploader.nativeElement.click();
      }
    });
  }

  async exportData() {
    this.processing.set(true);
    const tabName = this.tabs[this.selectedIndex()]?.name;
    if (!tabName) {
      this.processing.set(false);
      return;
    }
    const exportData = this.activeTab.getExportData();
    const rowsArray = [];
    exportData.items.forEach((item) => {
      const rowObj = {};
      exportData.columns.forEach((column) => {
        let value;
        if (column.itemProperty.includes('.')) {
          value = item;
          column.itemProperty.split('.').forEach((nestedProperty) => {
            value = value[nestedProperty];
          });
        } else if (typeof item[column.itemProperty] === 'boolean') {
          value = item[column.itemProperty] ? 'WAAR' : 'ONWAAR';
        } else {
          value = item[column.itemProperty];
        }
        rowObj[column.name] = value ?? '';
      });
      rowsArray.push(rowObj);
    });

    if (rowsArray.length > 0) {
      if (exportData.options.sortBy) {
        const sortBy = exportData.options.sortBy;
        rowsArray.sort((a, b) => {
          const aVal = String(a?.[sortBy] ?? '').toLowerCase();
          const bVal = String(b?.[sortBy] ?? '').toLowerCase();
          return aVal.localeCompare(bVal);
        });
      }

      await exportJsonToExcel(rowsArray, tabName, tabName);
    } else {
      this.snackBar.open(`Er zijn geen ${tabName.toLowerCase()} om te exporteren`, 'X', {
        duration: 5000,
      });
    }
    this.processing.set(false);
  }

  async uploadedFile(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }

    this.fileUploaded = file;
    await this.readExcel();
  }

  async readExcel() {
    this.processing.set(true);
    const importConfig = this.activeTab.getImportConfig();

    try {
      if (!importConfig.uniqueKey) {
        throw new Error('No unique key defined for import');
      }

      const spreadsheet = await readExcelFile(this.fileUploaded);
      if (!spreadsheet || Object.keys(spreadsheet).length === 0) {
        throw new Error('Spreadsheet is empty or could not be read');
      }

      const columnMapping = this.mapColumns(spreadsheet[1], importConfig);
      const result = this.processRows(spreadsheet, columnMapping, importConfig);
      await this.importData(result.importedData, importConfig, result.duplicateRows);
    } catch (error) {
      console.error('Import error:', error);
      this.snackBar.open('Er is iets misgegaan tijdens het verwerken van het bestand', 'X', {
        duration: 5000,
      });
    } finally {
      this.processing.set(false);
      this.uploader.nativeElement.value = '';
    }
  }

  private mapColumns(headerRow: any, importConfig: any): any {
    const columnMapping = {};
    Object.keys(headerRow).forEach((key) => {
      const cellValue = headerRow[key];
      if (!cellValue) return;
      const val = cellValue.toString().toLowerCase().trim();
      columnMapping[key] = importConfig.columnSettings.find(
        (col) => col.columnName === val,
      )?.itemProperty;
    });
    return columnMapping;
  }

  private processRows(
    spreadsheet: any,
    columnMapping: any,
    importConfig: any,
  ): { importedData: any[]; duplicateRows: boolean } {
    delete spreadsheet[1];
    const importedData: any[] = [];
    const requiredColumns = importConfig.columnSettings.filter((col) => col.required);
    let duplicateRows = false;

    for (const rowKey of Object.keys(spreadsheet)) {
      const rowObj = this.buildRowObject(spreadsheet[rowKey], columnMapping, importConfig);
      if (
        this.isValidRow(rowObj, requiredColumns) &&
        this.isUniqueRow(rowObj, importedData, importConfig)
      ) {
        importedData.push(rowObj);
      } else {
        duplicateRows = true;
      }
    }
    return { importedData, duplicateRows };
  }

  private buildRowObject(rowData: any, columnMapping: any, importConfig: any): any {
    const rowObj = {};
    for (const colKey of Object.keys(rowData)) {
      const colName = columnMapping[colKey];
      if (colName) {
        const colSetting = importConfig.columnSettings.find(
          (colSetting) => colSetting.itemProperty === colName,
        );
        let value =
          rowData[colKey] && rowData[colKey] !== ''
            ? rowData[colKey]
            : (colSetting?.defaultValue ?? null);
        value = this.convertValue(value, colSetting);

        if (colName.includes('.')) {
          this.setNestedProperty(rowObj, colName, value);
        } else {
          rowObj[colName] = value;
        }
      }
    }
    return rowObj;
  }

  private setNestedProperty(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
  }

  private convertValue(value: any, colSetting?: any): any {
    if (!colSetting?.type) return value;
    let convertedValue;
    switch (colSetting.type) {
      case 'boolean':
        convertedValue = value?.toString().toLowerCase() === 'waar' || value === true;
        break;
      case 'number':
        convertedValue = isNaN(Number(value)) ? (colSetting?.defaultValue ?? null) : Number(value);
        if (
          colSetting.min !== undefined &&
          convertedValue !== null &&
          convertedValue < colSetting.min
        ) {
          convertedValue = colSetting.min;
        }
        break;
      default:
        convertedValue = value;
    }
    return convertedValue;
  }

  private isValidRow(rowObj: any, requiredColumns: any[]): boolean {
    for (const reqColumn of requiredColumns) {
      const val = reqColumn.itemProperty
        .split('.')
        .reduce((current, key) => current?.[key], rowObj);
      if (val === null || val === '' || val === undefined) {
        return false;
      }
    }
    return true;
  }

  private isUniqueRow(rowObj: any, importedData: any[], importConfig: any): boolean {
    return !importedData.some(
      (item) => item[importConfig.uniqueKey] === rowObj[importConfig.uniqueKey],
    );
  }

  async importData(importedData: any[], importConfig: any, duplicateRows: boolean = false) {
    const collectionPaths = {
      vehicles: `organisations/${importConfig.organisationId}/vehicles`,
      vehicleGroups: `organisations/${importConfig.organisationId}/vehicleGroups`,
      rates: `organisations/${importConfig.organisationId}/products`,
    };
    const collectionPath = collectionPaths[importConfig.tab];

    try {
      if (importedData.length === 0) {
        throw new Error('No valid data available for import');
      }
      if (!this.checkConstraints(importedData, importConfig)) {
        return;
      }

      const validatedData = importedData;

      const batchSize = 50;
      let promises = [];
      let rowsSkipped = false;

      for (const item of validatedData) {
        if (!item[importConfig.uniqueKey]) {
          rowsSkipped = true;
          continue;
        }
        const existingData = importConfig.existingData.find(
          (existingItem) => existingItem[importConfig.uniqueKey] === item[importConfig.uniqueKey],
        );
        if (existingData) {
          promises.push(
            setDoc(doc(db, `${collectionPath}/${existingData.id}`), item, { merge: true }),
          );
        } else {
          promises.push(addDoc(collection(db, collectionPath), item));
        }
        if (promises.length === batchSize) {
          await Promise.allSettled(promises);
          promises = [];
        }
      }
      if (promises.length > 0) {
        await Promise.allSettled(promises);
      }

      this.showImportResult(rowsSkipped || duplicateRows, importConfig);
    } catch (error) {
      console.error('Import error:', error);
      this.snackBar.open('Er ging iets mis bij het verwerken van het bestand.', 'X', {
        duration: 5000,
      });
    }
  }

  private showImportResult(hasSkipped: boolean, importConfig: any) {
    if (hasSkipped) {
      this.dialog.open(WarningDialog, {
        width: '600px',
        data: {
          dialogType: 'importPartialSuccess',
        },
      });
    } else {
      const tabName = this.tabs.find((tab) => tab.key === importConfig.tab)?.name.toLowerCase();
      this.snackBar.open(`De ${tabName ?? 'gegevens'} zijn geïmporteerd.`, 'X', {
        duration: 5000,
      });
    }
  }

  checkConstraints(importedData: any[], importConfig: any): boolean {
    if (importConfig.tab === 'vehicles') {
      const newActive = importedData.filter(
        (item) =>
          item.active &&
          !importConfig.existingData.find(
            (existingItem) =>
              existingItem[importConfig.uniqueKey] === item[importConfig.uniqueKey] &&
              existingItem.active,
          ),
      ).length;
      const currentActive = importConfig.existingData.filter((item) => item.active).length;
      if (currentActive + newActive > dataStore.generalSettings.maxActiveVehicles) {
        this.dialog.open(WarningDialog, {
          width: '600px',
          data: {
            dialogType: 'importFailedCars',
            optionalData: {
              maxTotalVehicles: dataStore.generalSettings.maxActiveVehicles,
            },
          },
        });
        return false;
      }
    }

    if (importConfig.tab === 'rates') {
      const defaultRates = importedData.filter((item) => item.default);
      if (defaultRates.length > 1) {
        this.dialog.open(WarningDialog, {
          width: '600px',
          data: {
            dialogType: 'importFailedRates',
          },
        });
        return false;
      }
    }
    return true;
  }
}
